import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Heart, Loader2, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_ATTEMPTS = 6;
const POLL_INTERVAL = 2000;

const DonationSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState('checking'); // checking | success | pending | error
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState('error');
      return;
    }

    let cancelled = false;

    const poll = async (attempt) => {
      if (cancelled) return;
      if (attempt >= MAX_ATTEMPTS) {
        setState('pending');
        return;
      }
      try {
        const res = await axios.get(`${API}/donations/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === 'paid') {
          setAmount((res.data.amount_total / 100).toFixed(2));
          setState('success');
          return;
        }
        if (res.data.status === 'expired') {
          setState('error');
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      } catch (err) {
        if (!cancelled) setState('error');
      }
    };

    poll(0);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="pt-20 min-h-screen flex items-center justify-center">
      <div className="container">
        <div data-testid="donation-success-card" className="max-w-lg mx-auto text-center bg-white p-10 rounded-2xl shadow-sm border border-gray-200">
          {state === 'checking' && (
            <>
              <Loader2 className="h-14 w-14 text-green-600 mx-auto mb-6 animate-spin" />
              <h1 className="heading-2 mb-4">Confirming your donation...</h1>
              <p className="body-medium">Just a moment while we confirm your gift.</p>
            </>
          )}

          {state === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="heading-2 mb-4">Thank you so much! 💚</h1>
              <p className="body-large mb-2">
                Your generous donation{amount ? ` of $${amount}` : ''} has been received.
              </p>
              <p className="body-medium mb-8">
                Your support helps us build a stronger, more compassionate recovery community across
                Manistee and Mason Counties. We're grateful for you.
              </p>
              <div className="flex items-center justify-center gap-2 text-green-700 mb-8">
                <CheckCircle className="h-5 w-5" />
                <span className="body-small font-medium">Payment confirmed</span>
              </div>
              <Link to="/" className="btn-primary">Return Home</Link>
            </>
          )}

          {state === 'pending' && (
            <>
              <Loader2 className="h-14 w-14 text-green-600 mx-auto mb-6 animate-spin" />
              <h1 className="heading-2 mb-4">Processing your donation</h1>
              <p className="body-medium mb-8">
                Your payment is still processing. If you completed checkout, you'll receive a receipt by email
                shortly. Thank you for your generosity!
              </p>
              <Link to="/" className="btn-primary">Return Home</Link>
            </>
          )}

          {state === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="h-10 w-10 text-red-500" />
              </div>
              <h1 className="heading-2 mb-4">We couldn't confirm your donation</h1>
              <p className="body-medium mb-8">
                Something went wrong, or the donation was cancelled. No worries — you can try again any time.
                If you have questions, email info@sagedrecovery.org.
              </p>
              <Link to="/get-involved" className="btn-primary">Back to Donate</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DonationSuccessPage;
